import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import Button from "../../components/flat/ui/Button";
import AuthPasswordLayout, {
  ButtonText,
  Container,
  Div,
  ErrorMessage,
  StyledBadge,
  StyledInput,
  Subtitle,
  Title,
} from "../../modules/Auth/components/AuthPasswordLayout";
import CheckoutRegistrationLoader from "./components/CheckoutRegistrationLoader";
import {
  sendFirstAccessCode,
  waitFirstAccessRegistrationReady,
} from "./firstAccessCrud";
import {
  REGISTRATION_COMPLETE_MESSAGE,
  REGISTRATION_SENDING_CODE_MESSAGE,
  useRegistrationWaitProgress,
} from "./hooks/useRegistrationWaitProgress";
import {
  trackFirstAccessSignupStarted,
  trackFirstAccessSignupCompleted,
  trackFirstAccess2faCodeSent,
} from "./trackEvents";
import { FIRST_ACCESS_ROUTES } from "./routes";
import { useFirstAccessRecaptcha } from "./components/FirstAccessRecaptcha";

interface LocationState {
  email?: string;
  fromCheckout?: boolean;
  retryAt?: string | Date;
}

const AUTO_SEND_SESSION_KEY_PREFIX = "first-access:auto-send:";
const PENDING_REGISTRATION_KEY_PREFIX = "first-access:pending-registration:";
const RETRY_WAIT_MESSAGE =
  "Aguarde alguns instantes antes de solicitar um novo código.";
const FREEMIUM_ORIGIN = "freemium";
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmailFormat(email: string): boolean {
  return EMAIL_FORMAT_REGEX.test(email);
}

function getUrlParam(location: { search: string; hash: string }, key: string): string {
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
  const queryParams = new URLSearchParams(location.search);

  return (hashParams.get(key) || queryParams.get(key) || "").trim();
}

function getActiveRetryAtMs(retryAt?: string | Date): number | null {
  if (!retryAt) {
    return null;
  }

  const retryAtMs = new Date(retryAt).getTime();
  if (Number.isNaN(retryAtMs) || retryAtMs <= Date.now()) {
    return null;
  }

  return retryAtMs;
}

function getAutoSendSessionKey(email: string): string {
  return `${AUTO_SEND_SESSION_KEY_PREFIX}${email.trim().toLowerCase()}`;
}

function getPendingRegistrationKey(email: string): string {
  return `${PENDING_REGISTRATION_KEY_PREFIX}${email.trim().toLowerCase()}`;
}

type AutoSendPhase = "waiting" | "sending";

export default function EmailStep() {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const urlEmail = getUrlParam(location, "email");
  const isFreemiumOrigin = getUrlParam(location, "origem") === FREEMIUM_ORIGIN;
  const freemiumEmail =
    isFreemiumOrigin && isValidEmailFormat(urlEmail) ? urlEmail : undefined;
  const [email, setEmail] = useState(location.state?.email || urlEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoSending, setIsAutoSending] = useState(false);
  const [autoSendPhase, setAutoSendPhase] = useState<AutoSendPhase>("waiting");
  const [isWaitingRegistration, setIsWaitingRegistration] = useState(false);
  const [pendingRegistrationEmail, setPendingRegistrationEmail] = useState("");
  const [error, setError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaVersion, setRecaptchaVersion] = useState("");
  const [isCaptchaPending, setIsCaptchaPending] = useState(true);
  const [autoSendEmail, setAutoSendEmail] = useState("");
  const [retryAt, setRetryAt] = useState<string | Date | undefined>(
    location.state?.retryAt,
  );
  const [isRetryCooldownActive, setIsRetryCooldownActive] = useState(
    () => getActiveRetryAtMs(location.state?.retryAt) !== null,
  );
  const retryAtRef = useRef(retryAt);

  const handleRecaptchaTokenChange = useCallback(
    (token: string, version: string) => {
      setRecaptchaToken(token);
      setRecaptchaVersion(version);
    },
    [],
  );

  const {
    recaptchaField,
    isReady,
    handleInvalidRecaptcha,
    refreshRecaptcha,
  } = useFirstAccessRecaptcha({
    action: "first_access_send_code",
    onTokenChange: handleRecaptchaTokenChange,
    onPendingChange: setIsCaptchaPending,
  });

  const {
    progress: registrationProgress,
    message: registrationStatusMessage,
    smoothComplete: isRegistrationProgressCompleting,
    animateToComplete: animateRegistrationProgressToComplete,
    reset: resetRegistrationProgress,
  } = useRegistrationWaitProgress(isWaitingRegistration);

  const sendCodeAndNavigate = useCallback(
    async (targetEmail: string): Promise<boolean> => {
      if (getActiveRetryAtMs(retryAtRef.current) !== null) {
        setError(RETRY_WAIT_MESSAGE);
        return false;
      }

      if (!recaptchaToken || !recaptchaVersion) {
        setError("Aguarde a validação de segurança e tente novamente.");
        return false;
      }

      try {
        const { data: responseData } = await sendFirstAccessCode(targetEmail, {
          recaptchaToken,
          recaptchaVersion,
        });

        if (responseData?.data?.accessToken) {
          sessionStorage.removeItem(getPendingRegistrationKey(targetEmail));
          trackFirstAccessSignupCompleted({ email: targetEmail });
          trackFirstAccess2faCodeSent({ email: targetEmail });

          history.push(FIRST_ACCESS_ROUTES.VERIFICATION, {
            email: responseData.data.email || targetEmail,
            tempToken: responseData.data.accessToken,
          });
          return true;
        }

        if (responseData?.data?.retryAt) {
          setRetryAt(responseData.data.retryAt);
          setError(RETRY_WAIT_MESSAGE);
          return false;
        }

        setError(
          "Se o e-mail estiver cadastrado, você receberá um código de verificação.",
        );
        return false;
      } catch (err) {
        if (err?.response?.data?.error === "INVALID_RECAPTCHA") {
          handleInvalidRecaptcha();
          setError(
            "Não foi possível validar a verificação de segurança. Tente novamente.",
          );
          return false;
        }

        throw err;
      }
    },
    [recaptchaToken, recaptchaVersion, history, handleInvalidRecaptcha],
  );

  const sendCodeAndNavigateRef = useRef(sendCodeAndNavigate);
  const recaptchaRef = useRef({ recaptchaToken: "", recaptchaVersion: "" });

  useEffect(() => {
    sendCodeAndNavigateRef.current = sendCodeAndNavigate;
  }, [sendCodeAndNavigate]);

  useEffect(() => {
    retryAtRef.current = retryAt;
  }, [retryAt]);

  useEffect(() => {
    recaptchaRef.current = {
      recaptchaToken,
      recaptchaVersion,
    };
  }, [recaptchaToken, recaptchaVersion]);

  useEffect(() => {
    trackFirstAccessSignupStarted();
  }, []);

  useEffect(() => {
    if (!location.state?.retryAt) {
      return;
    }

    setRetryAt(location.state.retryAt);
  }, [location.state?.retryAt]);

  useEffect(() => {
    const retryAtMs = getActiveRetryAtMs(retryAt);
    if (retryAtMs === null) {
      setIsRetryCooldownActive(false);
      return;
    }

    setError(RETRY_WAIT_MESSAGE);
    setIsRetryCooldownActive(true);

    const timeoutId = window.setTimeout(() => {
      setIsRetryCooldownActive(false);
      setRetryAt(undefined);
      setError(current => (current === RETRY_WAIT_MESSAGE ? "" : current));
    }, retryAtMs - Date.now());

    return () => window.clearTimeout(timeoutId);
  }, [retryAt]);

  useEffect(() => {
    const registrationEmail = location.state?.email?.trim() || freemiumEmail;

    if (!registrationEmail) {
      return;
    }

    if (
      sessionStorage.getItem(getPendingRegistrationKey(registrationEmail)) ===
      "true"
    ) {
      setPendingRegistrationEmail(registrationEmail);
    }
  }, [location.state?.email, freemiumEmail]);

  useEffect(() => {
    const checkoutEmail = location.state?.email?.trim();
    const autoSendTriggerEmail = location.state?.fromCheckout
      ? checkoutEmail
      : freemiumEmail;

    if (!autoSendTriggerEmail || !isReady) {
      return;
    }

    const autoSendSessionKey = getAutoSendSessionKey(autoSendTriggerEmail);
    if (sessionStorage.getItem(autoSendSessionKey) === "started") {
      return;
    }

    sessionStorage.setItem(autoSendSessionKey, "started");
    setAutoSendEmail(autoSendTriggerEmail);
  }, [
    location.state?.email,
    location.state?.fromCheckout,
    freemiumEmail,
    isReady,
  ]);

  useEffect(() => {
    const checkoutEmail = autoSendEmail;

    if (!checkoutEmail) {
      return;
    }

    const abortController = new AbortController();
    let cancelled = false;

    const autoSendFromCheckout = async () => {
      setIsAutoSending(true);
      setAutoSendPhase("waiting");
      setIsWaitingRegistration(true);
      resetRegistrationProgress();

      if (getActiveRetryAtMs(retryAtRef.current) !== null) {
        setError(RETRY_WAIT_MESSAGE);
        setIsAutoSending(false);
        setIsWaitingRegistration(false);
        return;
      }

      setError("");

      try {
        const isReadyForSend = await waitFirstAccessRegistrationReady(
          checkoutEmail,
          {
            signal: abortController.signal,
            getRecaptcha: () => recaptchaRef.current,
            refreshRecaptcha,
          },
        );

        if (cancelled) return;

        if (isReadyForSend) {
          await animateRegistrationProgressToComplete();
        }

        if (cancelled) return;

        setIsWaitingRegistration(false);

        if (!isReadyForSend) {
          sessionStorage.setItem(
            getPendingRegistrationKey(checkoutEmail),
            "true",
          );
          setPendingRegistrationEmail(checkoutEmail);
          return;
        }

        setAutoSendPhase("sending");
        await sendCodeAndNavigateRef.current(checkoutEmail);
      } catch (err) {
        if (!cancelled && !abortController.signal.aborted) {
          setError(
            err?.response?.data?.message ||
              "Não foi possível enviar o código. Tente novamente.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsWaitingRegistration(false);
          setIsAutoSending(false);
        }
      }
    };

    autoSendFromCheckout();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [
    autoSendEmail,
    animateRegistrationProgressToComplete,
    refreshRecaptcha,
    resetRegistrationProgress,
  ]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (getActiveRetryAtMs(retryAt) !== null) {
      setError(RETRY_WAIT_MESSAGE);
      return;
    }

    setError("");

    const targetEmail = email.trim();

    if (!targetEmail) {
      setError("Informe seu e-mail para continuar.");
      return;
    }

    if (!isReady) {
      setError("Aguarde a validação de segurança e tente novamente.");
      return;
    }

    setIsLoading(true);

    try {
      await sendCodeAndNavigate(targetEmail);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Não foi possível enviar o código. Tente novamente.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled =
    isLoading || isCaptchaPending || !isReady || isRetryCooldownActive;

  const autoSendStatusMessage = isRegistrationProgressCompleting
    ? REGISTRATION_COMPLETE_MESSAGE
    : autoSendPhase === "sending"
    ? REGISTRATION_SENDING_CODE_MESSAGE
    : registrationStatusMessage;

  if (pendingRegistrationEmail) {
    return (
      <AuthPasswordLayout variant="banner">
        <Container>
          <Div>
            <Title>Não conseguimos concluir por aqui</Title>
            <Subtitle>
              Seu cadastro segue sendo processado. Assim que estiver pronto,
              enviaremos um e-mail para você com o código e o link para acessar a
              Jusfy. Pode fechar esta página — te avisamos por lá. Se não
              encontrar em alguns minutos, confira também a pasta de spam.
            </Subtitle>
            <StyledBadge content={pendingRegistrationEmail} />
          </Div>
        </Container>
      </AuthPasswordLayout>
    );
  }

  if (isAutoSending) {
    return (
      <CheckoutRegistrationLoader
        phase={autoSendPhase}
        progress={registrationProgress}
        statusMessage={autoSendStatusMessage}
        smoothComplete={isRegistrationProgressCompleting}
        email={autoSendEmail}
      />
    );
  }

  return (
    <AuthPasswordLayout variant="banner">
      <Container>
        <Div>
          <Title>Confirme seu e-mail</Title>
          <Subtitle>
            Informe o e-mail usado no checkout para receber o código de
            verificação e acessar a plataforma.
          </Subtitle>
        </Div>

        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Subtitle
            as="label"
            htmlFor="first-access-email"
            style={{ display: "block", textAlign: "left", marginBottom: 8 }}
          >
            E-mail
          </Subtitle>
          <StyledInput
            id="first-access-email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={event => setEmail(event.target.value)}
            disabled={isLoading}
            autoComplete="email"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "first-access-email-error" : undefined}
          />
          {error ? (
            <ErrorMessage id="first-access-email-error" role="alert">
              {error}
            </ErrorMessage>
          ) : null}
          {recaptchaField}

          <Button
            type="submit"
            width="100%"
            padding="10px 40px"
            borderRadius="5px"
            hoverBackgroundColor="#007656"
            disabled={isSubmitDisabled}
            style={{ marginTop: 16 }}
          >
            <ButtonText>
              {isLoading ? (
                <TailSpin
                  visible
                  height="20"
                  width="20"
                  color="#ffffff"
                  ariaLabel="loading"
                />
              ) : (
                "Continuar"
              )}
            </ButtonText>
          </Button>
        </form>
      </Container>
    </AuthPasswordLayout>
  );
}
